// Mock data for AI & Machine Learning Course Landing Page

export const courseInfo = {
  title: "Master AI & Machine Learning",
  subtitle: "From Fundamentals to Advanced Applications",
  description: "Transform your career with our comprehensive AI & Machine Learning program. Learn from industry experts and build real-world projects that matter.",
  stats: [
    { label: "Students Enrolled", value: "12,500+" },
    { label: "Course Hours", value: "120+" },
    { label: "Projects", value: "25+" },
    { label: "Job Placement", value: "94%" }
  ],
  videoPreviewUrl: "https://example.com/course-preview",
  price: "$499",
  originalPrice: "$999"
};

export const syllabus = [
  {
    id: 1,
    module: "Module 1",
    title: "Foundations of AI & Machine Learning",
    duration: "3 weeks",
    topics: [
      "Introduction to Artificial Intelligence",
      "History and Evolution of AI",
      "Types of Machine Learning: Supervised, Unsupervised, Reinforcement",
      "Python Programming for AI",
      "Mathematics for ML: Linear Algebra & Statistics",
      "Setting Up Your AI Development Environment"
    ]
  },
  {
    id: 2,
    module: "Module 2",
    title: "Data Science & Preprocessing",
    duration: "2 weeks",
    topics: [
      "Data Collection and Web Scraping",
      "Exploratory Data Analysis (EDA)",
      "Data Cleaning and Transformation",
      "Feature Engineering Techniques",
      "Working with Pandas & NumPy",
      "Data Visualization with Matplotlib & Seaborn"
    ]
  },
  {
    id: 3,
    module: "Module 3",
    title: "Classical Machine Learning Algorithms",
    duration: "4 weeks",
    topics: [
      "Linear & Logistic Regression",
      "Decision Trees & Random Forests",
      "Support Vector Machines (SVM)",
      "K-Nearest Neighbors (KNN)",
      "Naive Bayes Classifier",
      "Ensemble Methods & Boosting",
      "Model Evaluation & Cross-Validation"
    ]
  },
  {
    id: 4,
    module: "Module 4",
    title: "Deep Learning Fundamentals",
    duration: "4 weeks",
    topics: [
      "Neural Network Architecture",
      "Activation Functions & Backpropagation",
      "TensorFlow & PyTorch Frameworks",
      "Convolutional Neural Networks (CNNs)",
      "Recurrent Neural Networks (RNNs)",
      "Transfer Learning Techniques",
      "Hyperparameter Tuning"
    ]
  },
  {
    id: 5,
    module: "Module 5",
    title: "Natural Language Processing",
    duration: "3 weeks",
    topics: [
      "Text Preprocessing & Tokenization",
      "Word Embeddings: Word2Vec, GloVe",
      "Sequence Models & Attention Mechanisms",
      "Transformer Architecture Deep Dive",
      "BERT, GPT & Large Language Models",
      "Building Chatbots & Text Classifiers"
    ]
  },
  {
    id: 6,
    module: "Module 6",
    title: "Computer Vision",
    duration: "3 weeks",
    topics: [
      "Image Processing Fundamentals",
      "Object Detection & YOLO",
      "Image Segmentation Techniques",
      "Face Recognition Systems",
      "Generative Adversarial Networks (GANs)",
      "Real-time Video Analysis"
    ]
  },
  {
    id: 7,
    module: "Module 7",
    title: "Advanced AI Topics",
    duration: "3 weeks",
    topics: [
      "Reinforcement Learning & Q-Learning",
      "Generative AI & Diffusion Models",
      "AI Ethics & Responsible AI",
      "MLOps & Model Deployment",
      "AI in Production: Scaling & Monitoring",
      "Edge AI & Mobile Deployment"
    ]
  },
  {
    id: 8,
    module: "Module 8",
    title: "Capstone Projects & Career Prep",
    duration: "2 weeks",
    topics: [
      "Industry Capstone Project",
      "Portfolio Building & GitHub Optimization",
      "AI Interview Preparation",
      "Resume Building for AI Roles",
      "Networking in the AI Community",
      "Career Pathways in AI/ML"
    ]
  }
];

export const previewModules = [
  {
    id: 1,
    title: "Neural Networks Explained",
    description: "Understand how neural networks learn and make predictions",
    icon: "Brain",
    duration: "15 min preview"
  },
  {
    id: 2,
    title: "Build Your First Model",
    description: "Hands-on tutorial creating an image classifier",
    icon: "Code",
    duration: "20 min preview"
  },
  {
    id: 3,
    title: "Real-World AI Applications",
    description: "See AI in action across different industries",
    icon: "Rocket",
    duration: "12 min preview"
  },
  {
    id: 4,
    title: "Career in AI/ML",
    description: "Explore career paths and salary expectations",
    icon: "TrendingUp",
    duration: "10 min preview"
  }
];

export const faqs = [
  {
    id: 1,
    question: "Do I need prior programming experience?",
    answer: "While some programming knowledge is helpful, our course starts with Python fundamentals. We recommend basic familiarity with any programming language, but motivated beginners can succeed with dedication and our comprehensive foundation module."
  },
  {
    id: 2,
    question: "What math background is required?",
    answer: "We cover essential mathematics (linear algebra, calculus, statistics) as part of the curriculum. High school math knowledge is sufficient to start. Our approach focuses on intuitive understanding before diving into mathematical details."
  },
  {
    id: 3,
    question: "How long do I have access to the course?",
    answer: "You get lifetime access to all course materials, including future updates. This means you can learn at your own pace and revisit concepts whenever needed throughout your career."
  },
  {
    id: 4,
    question: "What projects will I build?",
    answer: "You'll build 25+ projects including: image classifiers, sentiment analyzers, recommendation systems, chatbots, object detection systems, and a comprehensive capstone project. All projects use real-world datasets and industry-standard tools."
  },
  {
    id: 5,
    question: "Is there job placement support?",
    answer: "Yes! We offer career services including resume reviews, mock interviews, portfolio guidance, and access to our hiring partner network. Our 94% job placement rate speaks to the effectiveness of our career support."
  },
  {
    id: 6,
    question: "Can I get a certificate upon completion?",
    answer: "Absolutely. Upon completing the course and capstone project, you'll receive a verified certificate that you can share on LinkedIn and include in your resume. Many of our graduates report that this certificate helped them land AI/ML roles."
  },
  {
    id: 7,
    question: "What if I fall behind or need extra help?",
    answer: "We offer weekly live Q&A sessions, an active community forum, and optional 1-on-1 mentoring sessions. Our support team typically responds within 24 hours, and you can always revisit any module at your own pace."
  },
  {
    id: 8,
    question: "Is there a money-back guarantee?",
    answer: "Yes, we offer a 30-day money-back guarantee. If you're not satisfied with the course for any reason within the first 30 days, we'll provide a full refund—no questions asked."
  }
];

export const testimonials = [
  {
    id: 1,
    name: "Sarah Chen",
    role: "ML Engineer at Google",
    content: "This course transformed my career. The hands-on projects gave me the confidence to ace my interviews.",
    avatar: "SC"
  },
  {
    id: 2,
    name: "Michael Roberts",
    role: "Data Scientist at Netflix",
    content: "The curriculum is incredibly comprehensive. I went from knowing basic Python to deploying ML models in production.",
    avatar: "MR"
  },
  {
    id: 3,
    name: "Priya Sharma",
    role: "AI Research Lead",
    content: "Best investment I've made in my education. The NLP and Computer Vision modules are world-class.",
    avatar: "PS"
  }
];

export const chatbotResponses = {
  greeting: "Hi! I'm your AI Course Assistant. I can help you with questions about the course, syllabus, pricing, or enrollment. What would you like to know?",
  defaultResponse: "I'm here to help! Feel free to ask about our course curriculum, pricing, enrollment process, or career support."
};
